import type { DocumentTypeInput } from '@chukua/common';
import { Button, Table } from 'antd';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { TableSkeleton } from '../../components/TableSkeleton';
import { buildDocumentTypeColumns } from './build-document-type-columns';
import { DocumentTypeFormDrawer } from './DocumentTypeFormDrawer';
import type { DocumentType } from './reference-data.api';

interface DocumentTypesPanelProps {
  types: DocumentType[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: DocumentTypeInput) => Promise<boolean>;
  onUpdate: (id: string, input: DocumentTypeInput) => Promise<boolean>;
  onDeactivate: (row: DocumentType) => Promise<void>;
  onReactivate: (row: DocumentType) => Promise<void>;
  onResetSaveError: () => void;
}

/** Client-side paging (Ant's default) — the small-fixed-catalogue exception to server-side lists (CLAUDE.md → Lists and tables). */
export function DocumentTypesPanel({
  types,
  loading,
  saveError,
  saving,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  onResetSaveError,
}: DocumentTypesPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentType | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: DocumentType) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildDocumentTypeColumns({ onEdit: openEdit, onDeactivate, onReactivate });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button type="primary" icon={<Plus className="size-4" />} onClick={openCreate}>
          New document type
        </Button>
      </div>
      {loading ? (
        <TableSkeleton label="Loading document types" />
      ) : (
        <Table rowKey="id" dataSource={types} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }} />
      )}
      <DocumentTypeFormDrawer
        open={drawerOpen}
        editing={editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
